import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, Stack, Box, Typography, Card, CardContent, Alert, Fab } from '@mui/material';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import AddIcon from '@mui/icons-material/Add';
import { ETransportTargetFreightPlutoDto, MinimalTarget } from '@/generated/model';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { useTranslation } from 'react-i18next';
import { mapAvaliableTargetIDs, getPlateNumber } from './utils/utils';
import { DateTimePickerSelect } from '@/components/common/date/DateTimePickerSelect';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import './etransport.css';

export function TargetFreightsSubmitForm({
  uit,
  minDate,
  maxDate,
  targets,
  setNewChanges,
  setOpenTargetFreightSubmitForm,
}: {
  uit: string | undefined;
  minDate?: Date;
  maxDate?: Date;
  targets: MinimalTarget[] | undefined;
  setNewChanges: Dispatch<SetStateAction<FreightDataChange | null>>;
  setOpenTargetFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [currentValue, setCurrentValue] = useState<Date | null>(null);
  const [targetFreightFormData, setTargetFreightFormData] = useState<ETransportTargetFreightPlutoDto>({
    creationTimeMicros: undefined,
    startTimeMicros: undefined,
    endTimeMicros: undefined,
    targetId: undefined,
    uit: uit,
    trailer1: '', //Can't be undefined because its part of a TextField
    trailer2: '', //Can't be undefined because its part of a TextField
  });
  const isFormIncomplete: boolean = !targetFreightFormData.targetId || !targetFreightFormData.startTimeMicros;

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const initialHeight = viewport.height;
    const handler = () => setIsKeyboardOpen(viewport.height < initialHeight * 0.75);
    viewport.addEventListener('resize', handler);
    return () => viewport.removeEventListener('resize', handler);
  }, []);

  function handleSubmit(): void {
    setNewChanges((prev) => ({
      ...prev,
      uit: targetFreightFormData.uit,
      targetFreights: targetFreightFormData,
    }));
    handleClose();
  }

  function handleClose(): void {
    setOpenTargetFreightSubmitForm(false);
  }

  function handleDatePicker(date: Date) {
    if (!date) return;
    const validityStartTimeMicros = date.getTime() * 1000;
    setTargetFreightFormData({
      ...targetFreightFormData,
      startTimeMicros: validityStartTimeMicros,
      endTimeMicros: 0,
    });
    setCurrentValue(date);
  }

  const avaliableTargetIDs = useMemo(() => {
    return mapAvaliableTargetIDs(targets);
  }, [targets]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: isPhoneOrHighScreen() ? '100%' : '99vh',
        borderRadius: isPhoneOrHighScreen() ? '0px' : '5px',
        backgroundColor: 'var(--mui-palette-contrast-background)',
      }}
    >
      <div className={`topBar ${isPhoneOrHighScreen() ? 'phone' : ''}`}>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', gap: 1 }}>
          <ArrowBackIcon
            sx={{ fontSize: '1.5rem', color: 'text.secondary', cursor: 'pointer' }}
            onClick={handleClose}
          />
          <Typography variant="h6" sx={{ color: 'black' }}>
            {t('etransport.targetform.new')}
          </Typography>
        </Box>
      </div>
      {isFormIncomplete && <Alert severity="warning">{t('etransport.freightfrom.warning')}</Alert>}
      <div
        className="panelOverlay"
        style={{
          paddingBottom: isPhoneOrHighScreen() ? '112px' : '0px',
          paddingLeft: isPhoneOrHighScreen() ? '1%' : '0',
        }}
      >
        <Card
          elevation={3}
          sx={{
            padding: '1px',
            height: isPhoneOrHighScreen() ? 'auto' : '100%',
            marginLeft: isPhoneOrHighScreen() ? '1%' : '0',
            marginRight: isPhoneOrHighScreen() ? '1%' : '0',
          }}
        >
          <CardContent>
            <Stack spacing={3}>
              <Autocomplete
                options={avaliableTargetIDs}
                getOptionLabel={(option) => {
                  return getPlateNumber(targets, option) || 'Unkown';
                }}
                value={targetFreightFormData.targetId ?? null}
                onChange={(_, newValue) => {
                  setTargetFreightFormData({ ...targetFreightFormData, targetId: newValue || undefined });
                }}
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('etransport.freightfrom.plate')}
                    placeholder={t('etransport.freightfrom.plateselect')}
                    required
                  />
                )}
              />

              <DateTimePickerSelect
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
                label={t('etransport.freightfrom.validitystart') + ' *'}
                minDate={minDate}
                maxDate={maxDate}
                currentValue={currentValue}
                setSelectedDate={handleDatePicker}
              />

              <TextField
                label={t('etransport.freightfrom.trailer1')}
                value={targetFreightFormData.trailer1}
                onChange={(e) => {
                  setTargetFreightFormData({ ...targetFreightFormData, trailer1: e.target.value });
                }}
                placeholder={t('etransport.freightfrom.platewrite')}
                fullWidth
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              />

              <TextField
                label={t('etransport.freightfrom.trailer2')}
                value={targetFreightFormData.trailer2}
                onChange={(e) => {
                  setTargetFreightFormData({ ...targetFreightFormData, trailer2: e.target.value });
                }}
                placeholder={t('etransport.freightfrom.platewrite')}
                fullWidth
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              />
            </Stack>
          </CardContent>
        </Card>
      </div>

      <Stack
        spacing={1}
        sx={{
          position: 'absolute',
          bottom: isPhoneOrHighScreen() ? 15 : 20,
          right: isPhoneOrHighScreen() ? 10 : 18,
          display: isKeyboardOpen ? 'none' : undefined,
        }}
      >
        <>
          <Fab
            variant="extended"
            onClick={handleSubmit}
            aria-label={t('etransport.freightfrom.save')}
            disabled={isFormIncomplete}
            sx={{
              boxShadow: 6,
              backgroundColor: 'var(--mui-palette-secondary-main)',
              color: 'white',
              border: '2px solid white',
              '&:hover': {
                backgroundColor: 'var(--mui-palette-secondary-light)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'grey',
                color: 'white',
                opacity: 1,
              },
            }}
          >
            <AddIcon sx={{ mr: 1 }} /> {t('etransport.targetform.add')}
          </Fab>
          <Fab
            variant="extended"
            onClick={handleClose}
            aria-label={t('etransport.freightfrom.cancel')}
            sx={{
              boxShadow: 6,
              backgroundColor: 'white',
              color: 'var(--mui-palette-secondary-main)',
              border: '2px solid var(--mui-palette-secondary-main)',
              '&:hover': {
                backgroundColor: 'rgba(146, 102, 21, 0.1)',
              },
            }}
          >
            <HighlightOffOutlinedIcon sx={{ mr: 1 }} />
            {t('etransport.freightfrom.cancel')}
          </Fab>
        </>
      </Stack>
    </Box>
  );
}
